import React, { useEffect, useMemo, useState } from "react";
import {
    Dialog,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/Modal";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm, FormProvider } from "react-hook-form";
import { X } from "lucide-react";
import { ButtonShadcn as Button } from "@/components/ui/Button";
import RHFInput from "@/components/form/RHFInput";
import RHFTextArea from "@/components/form/RHFTextArea";
import RHFSelect from "@/components/form/RHFSelect";
import RHFCheckboxGroup from "@/components/form/RHFCheckboxGroup";
import toast from "react-hot-toast";
import { PermissionsService } from "@/services/permissionsService";
import { RoleService } from "@/services/roleService";
import { roleCreateFormSchema, roleFormSchema } from "../validation.schema";
import { groupPermissionsByCategory } from "../permission-category";
import {
    getPermissionRuleState,
    withCategoryAllCleared,
    withCategoryAllSelected,
    withPermissionDependencies,
} from "../permission-rules";
import { parentRoleOptions } from "../parent-options";
import { apiErrorMessageFormat } from "@/utils/formErrorUtils";
import { useTenantScopeParams } from "@/hooks/useTenantScope";

const INPUT_STYLE = "w-full rounded-lg px-4 py-2.5 text-sm placeholder:text-text-muted bg-background-form/40 focus:outline-none transition text-text-primary";

const AddEditRoleModal = ({
    isOpen,
    mode = "add",
    onModelClose,
    onHandleSubmit,
    defaultValues,
    // The role being edited. Its own row and everything below it cannot be its
    // parent — the route refuses both with 422 ROLE_CYCLE — so the dropdown
    // does not offer them. Mirrors `editUserId` on AddEditUserModal.
    editRoleId = null,
}) => {
    // The Parent Role dropdown must come from the tenant the page is scoped
    // to. Unscoped, a SuperAdmin got every shared tenant's roles at once —
    // each name repeated per tenant — and none for a dedicated tenant.
    const { scopeParams } = useTenantScopeParams();

    const methods = useForm({
        // Creating a role requires a parent; editing one leaves it alone when
        // the field is untouched, which is what lets the top-of-tree role be
        // renamed at all.
        resolver: zodResolver(mode === "edit" ? roleFormSchema : roleCreateFormSchema),
        mode: "onChange",
        defaultValues,
    });

    const {
        handleSubmit,
        formState: { isSubmitting },
        reset,
        watch,
        setValue,
    } = methods;

    const permissionsSelected = watch("permissions") || [];

    const [parentRoleList, setParentRoleList] = useState([]);
    const [parentLoading, setParentLoading] = useState(false);

    const [permissionsByCategory, setPermissionsByCategory] = useState({});
    const [permissionsLoading, setPermissionsLoading] = useState(false);

    const formatCategoryLabel = (cat) =>
    cat
        .replace(/[_-]+/g, " ")
        .split(" ")
        .filter(Boolean)
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ");

    const fetchFromDetails = async () => {
        try {
            setParentLoading(true);
            setPermissionsLoading(true);

            const [rolesRes, permissionsRes] = await Promise.all([
                RoleService.fetchRolesList({
                    pagination: false,
                    ...scopeParams,
                }),

                PermissionsService.fetchPermissionsList({
                    pagination: false,
                    ...scopeParams,
                    // Only the active catalogue. Codes a coarser grant already
                    // covers are inactive on purpose (see the server's
                    // INTERVIEW_GRANT_EXPANSION) and must not appear as their own
                    // checkbox - a box you tick that is implied anyway, or that
                    // grants nothing on its own, is worse than no box.
                    status: true,
                }),
            ]);

            // Roles
            const roles = rolesRes?.data?.roles ?? [];

            const roleOptions = parentRoleOptions(roles, editRoleId);

            // Permissions
            // Sourcing, Extension, Interview Scheduling and CV Formatting are
            // disabled on the frontend — commented out, not deleted, across
            // the app (see PLAN doc "resilient-stirring-tulip"). The backend
            // still serves their permission codes, so they're filtered out
            // here — `interview.manage` stays: it also gates the unrelated
            // candidate-profile "Reupload/Edit" actions.
            const isDisabledPermissionCode = (code) =>
                code?.startsWith("campaign.") ||
                code?.startsWith("extension.") ||
                code?.startsWith("resume_template.") ||
                (code?.startsWith("interview.") && code !== "interview.manage");
            const allPermissions = (permissionsRes?.data?.permissions ?? []).filter((p) => !isDisabledPermissionCode(p.code));

            const grouped = groupPermissionsByCategory(allPermissions);

            setParentRoleList(roleOptions);
            setPermissionsByCategory(grouped);

            if (Array.isArray(defaultValues?.permissions)) {
                setValue("permissions", defaultValues.permissions);
            }
        } catch (e) {
            console.error(e);

            toast.error(apiErrorMessageFormat(e, `Failed to load form data`));
        } finally {
            setParentLoading(false);
            setPermissionsLoading(false);
        }
    };

    // Load roles + permissions on modal open
    useEffect(() => {
        if (!isOpen) return;

        reset(defaultValues);

        fetchFromDetails();
    }, [isOpen, scopeParams, editRoleId]);

    const categories = useMemo(
        () => Object.keys(permissionsByCategory || {}),
        [permissionsByCategory]
    );

    // Select All / Deselect All go through the same dependency rule a single
    // checkbox does. Writing the raw union here was the one path around it,
    // and it left Create/Update/Delete ticked AND disabled beside Manage.
    const onHandleAllPermission = (perms) => {
        const allSelected = isCategoryFullySelected(perms, permissionsSelected);

        const updated = allSelected
            ? withCategoryAllCleared(perms, permissionsSelected)
            : withCategoryAllSelected(perms, permissionsSelected);

        setValue("permissions", updated, { shouldValidate: true });
    };

    // "Everything this category can hold", which is not "every code in it":
    // a `manage` grant subsumes its granular codes, so the button flips to
    // Deselect All once the sanitized set is reached rather than never.
    const isCategoryFullySelected = (perms, selected) => {
        const target = withCategoryAllSelected(perms, selected);
        return target.every((code) => selected.includes(code));
    };

    return (
        <Dialog
            open={isOpen}
            onOpenChange={(nextOpen) => {
                if (!nextOpen) onModelClose?.();
            }}
        >
            <DialogContent className="w-[90%] md:w-full flex flex-col">
                <DialogHeader className="relative px-5 py-3 border-b border-border-primary shrink-0">
                    <div>
                        <DialogTitle className="text-[15px] font-semibold text-text-primary">
                            {mode === "edit"
                                ? "Edit Role"
                                : "Add Role"}
                        </DialogTitle>

                        <DialogDescription className="text-[12px] text-text-primary/70 mt-0.5">
                            {mode === "edit"
                                ? "Update role details"
                                : "Create a new role"}
                        </DialogDescription>
                    </div>

                    <DialogClose className="absolute right-5 top-1/2 -translate-y-1/2 w-7 h-7 rounded-full bg-background-icon/40 flex items-center justify-center text-text-primary hover:bg-background-icon/60 transition-colors">
                        <span className="w-7 h-7 rounded-full bg-background-icon/40 flex items-center justify-center text-text-primary hover:bg-background-icon/60 transition-colors">
                            <X size={16} strokeWidth={3} />
                        </span>
                    </DialogClose>
                </DialogHeader>

                <div className="flex-1 overflow-y-auto custom-scrollbar">
                    <FormProvider {...methods}>
                        <form onSubmit={handleSubmit((formData) => onHandleSubmit?.(formData))}                        >
                            <div className="px-5 py-3">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">

                                    {/* Name */}
                                    <RHFInput
                                        name="name"
                                        label="Name"
                                        placeholder="e.g. Tenant Viewer"
                                        inputClass={INPUT_STYLE}
                                        required
                                    />

                                    {/* Department */}
                                    <RHFInput
                                        name="departmentScope"
                                        label="Department"
                                        placeholder="e.g. HR"
                                        inputClass={INPUT_STYLE}
                                        required
                                    />

                                    {/* Parent Role */}
                                    <RHFSelect
                                        name="parentRoleId"
                                        label="Parent Role"
                                        placeholder={
                                            parentLoading
                                                ? "Loading..."
                                                : "Select parent role"
                                        }
                                        options={parentRoleList}
                                        required={mode !== "edit"}
                                    />

                                    {/* Description */}
                                    <div className="md:col-span-2">
                                        <RHFTextArea
                                            name="description"
                                            label="Description"
                                            placeholder="Role description..."
                                            inputClass={INPUT_STYLE}
                                            required
                                            rows={4}
                                        />
                                    </div>
                                </div>

                                {/* Permissions */}
                                <div className="mt-6">
                                    <div className="text-sm font-semibold text-text-primary mb-3">
                                        Permissions
                                    </div>

                                    {permissionsLoading ? (
                                        <div className="text-xs text-text-secondary">
                                            Loading permissions…
                                        </div>
                                    ) : (
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 gap-y-4 items-stretch">
                                            {categories.length ? (
                                                categories.map((cat) => {
                                                    const perms = permissionsByCategory[cat] || [];
                                                    const ruleState = getPermissionRuleState(perms, permissionsSelected);

                                                    return (
                                                        <div
                                                            key={cat}
                                                            className="border border-border-primary/70 rounded-xl p-4 pb-0 bg-background-icon/10 flex flex-col"
                                                        >
                                                            <div className="flex items-center justify-between gap-3">
                                                                <div className="text-sm font-semibold text-text-primary capitalize">
                                                                     {formatCategoryLabel(cat)}
                                                                </div>

                                                                <div className="text-xs text-text-secondary flex gap-3 items-center">
                                                                    {perms.filter((p) => permissionsSelected.includes(p.code)).length}
                                                                    /
                                                                    {perms.length}
                                                                </div>

                                                            </div>

                                                            <div className="mt-3">
                                                                <RHFCheckboxGroup
                                                                    name="permissions"
                                                                    label={null}
                                                                    options={perms}
                                                                    getOptionValue={(p) => p.code}
                                                                    getOptionLabel={(p) => p.displayName || p.code}
                                                                    getOptionDisabled={(p) => ruleState[p.code]?.disabled}
                                                                    getOptionHint={(p) => ruleState[p.code]?.reason}
                                                                    sanitizeSelected={(next, opt, selected) =>
                                                        withPermissionDependencies(perms, next, opt, selected)
                                                    }
                                                                />
                                                            </div>

                                                            <div className="flex justify-end mt-auto pt-3">
                                                                <Button
                                                                    type="button"
                                                                    variant="outline"
                                                                    className="text-xs font-medium text-blue-500 hover:text-blue-600 p-0 bg-transparent border-none hover:bg-transparent"
                                                                    onClick={() => onHandleAllPermission(perms)}
                                                                >
                                                                    {isCategoryFullySelected(perms, permissionsSelected)
                                                                        ? "Deselect All"
                                                                        : "Select All"}
                                                                </Button>
                                                            </div>
                                                        </div>
                                                    );
                                                })
                                            ) : (
                                                <div className="text-xs text-text-secondary">
                                                    No permissions available.
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>

                                {/* Actions */}
                                <div className="flex justify-end gap-3 mt-6">
                                    <DialogClose
                                        render={
                                            <Button
                                                variant="outline"
                                                className="bg-button-tertiary border-none"
                                                type="button"
                                            >
                                                Cancel
                                            </Button>
                                        }
                                    />

                                    <Button
                                        className="bg-button-secondary text-white"
                                        type="submit"
                                        disabled={isSubmitting}
                                    >
                                        {isSubmitting
                                            ? "Saving…"
                                            : mode === "edit"
                                                ? "Update Role"
                                                : "Add Role"
                                        }
                                    </Button>
                                </div>
                            </div>
                        </form>
                    </FormProvider>
                </div>
            </DialogContent>
        </Dialog>
    );
};

export default AddEditRoleModal;